import { useMemo } from "react";
import {
  AutocompleteInput,
  Loading,
  useGetList,
  useRecordContext,
  type RaRecord,
} from "react-admin";

type GatedRelayEvent = RaRecord & {
  name?: string;
  requiresAccessGrant?: boolean;
};

type LinkedNiyaazEvent = RaRecord & {
  relayEventId?: string | null;
};

const relayEventOptionText = (record: GatedRelayEvent) => record.name ?? "—";

const sortGatedRelayEventsByNameDesc = (events: GatedRelayEvent[]) =>
  [...events].sort((a, b) => String(b.name ?? "").localeCompare(String(a.name ?? "")));

export const EventRelayEventField = () => {
  const record = useRecordContext<LinkedNiyaazEvent>();
  const currentEventId = record?.id;

  const { data: relayEvents = [], isPending: relayEventsPending } = useGetList<GatedRelayEvent>(
    "relay-events",
    {
      pagination: { page: 1, perPage: 100 },
      filter: { requiresAccessGrant: true },
    }
  );

  const { data: linkedEvents = [], isPending: linkedEventsPending } = useGetList<LinkedNiyaazEvent>(
    "events",
    {
      pagination: { page: 1, perPage: 200 },
    }
  );

  const gatedRelayEvents = useMemo(() => {
    const usedRelayEventIds = new Set(
      linkedEvents
        .filter((event) => event.relayEventId && event.id !== currentEventId)
        .map((event) => String(event.relayEventId))
    );

    return sortGatedRelayEventsByNameDesc(
      relayEvents.filter(
        (event) => event.requiresAccessGrant && !usedRelayEventIds.has(String(event.id))
      )
    );
  }, [relayEvents, linkedEvents, currentEventId]);

  if (relayEventsPending || linkedEventsPending) {
    return <Loading />;
  }

  return (
    <AutocompleteInput
      source="relayEventId"
      label="Relay event"
      choices={gatedRelayEvents}
      optionText={relayEventOptionText}
      fullWidth
      emptyText="None"
      parse={(value) => (value === "" || value == null ? null : value)}
      format={(value) => value ?? ""}
      helperText={
        gatedRelayEvents.length === 0
          ? "No available gated relay events (each relay event links to one niyaaz event only)"
          : "Optional link to the relay stream for this Ashara (one relay event per niyaaz event)"
      }
    />
  );
};
